import React from "react";
import { useNavigate } from "react-router-dom";
import {
  FaArrowLeft,
  FaEnvelope,
  FaUserCheck,
  FaDatabase,
  FaExclamationTriangle,
  FaShieldAlt,
  FaCreditCard,
  FaSyncAlt,
  FaGavel,
  FaCheckCircle,
  FaRobot,
  FaChartLine,
  FaGlobe,
  FaFileContract,
  FaVial,
  FaGem,
  FaCrown,
} from "react-icons/fa";

export default function TermsOfUse() {
  const navigate = useNavigate();

  const handleGoBack = () => navigate(-1);
  const handleContact = () => navigate("/contact");
  const handleTermsOfUse = () => navigate("/terms-of-use");
  const handlePrivacy = () => navigate("/privacy");

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950">
      {/* Header */}
      <header className="sticky top-0 z-50 border-b border-white/10 bg-slate-950/90 backdrop-blur-xl">
        <div className="mx-auto max-w-7xl px-3 py-3 md:px-6">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <button
              onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
              className="flex items-center shrink-0"
            >
              <img
                src="/VisionRoyale_logo.png"
                alt="Vision Royale"
                className="h-8 w-auto md:h-12 object-contain"
              />
            </button>

            {/* Buttons */}
            <div className="flex items-center gap-1.5 shrink-0">
              <button
                onClick={handleGoBack}
                className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg border border-white/20 bg-purple-400/10 px-3 py-1.5 sm:px-4 md:px-5 md:py-2 text-[10px] sm:text-sm font-semibold text-purple-300 transition-all hover:scale-105 hover:shadow-lg hover:shadow-purple-600/30"
              >
                <FaArrowLeft className="text-xs" />
                Back
              </button>
              <button
                onClick={handleContact}
                className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg border border-white/20 bg-green-400/10 px-3 py-1.5 sm:px-4 md:px-5 md:py-2 text-[10px] sm:text-sm font-semibold text-green-300 transition-all hover:scale-105 hover:shadow-lg hover:shadow-emerald-600/30"
              >
                <FaEnvelope />
                Contact
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-4xl px-4 py-8 md:px-6 md:py-12 lg:py-16">
        {/* Hero Section */}
        <div className="mb-8 md:mb-12 text-center">
          <div className="inline-flex items-center justify-center gap-2 rounded-full border border-emerald-400/30 bg-emerald-400/10 px-4 py-1.5 text-sm font-semibold text-emerald-300 mb-4">
            <FaFileContract className="text-xs" />
            <span>Legal Agreement</span>
          </div>
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-white">
            Terms of{" "}
            <span className="bg-gradient-to-r from-emerald-400 to-green-400 bg-clip-text text-transparent">
              Use
            </span>
          </h1>
          <p className="mt-4 text-white/60 text-sm md:text-base">
            Last Updated: May 2026
          </p>
        </div>

        {/* Terms Content */}
        <div className="space-y-6 md:space-y-8">
          {/* Welcome Section */}
          <div className="rounded-2xl border border-white/10 bg-white/5 p-5 md:p-6">
            <p className="text-white/80 text-sm md:text-base leading-relaxed">
              Welcome to{" "}
              <span className="text-emerald-400 font-semibold">
                Vision Royale
              </span>{" "}
              Service.
            </p>
            <p className="mt-3 text-white/70 text-sm md:text-base leading-relaxed">
              Vision Royale is a subscription-based web application that
              provides football statistics, mathematical models, AI-generated
              analyses, and value event detection for informational and
              educational purposes only. The Service does not offer sports
              betting, gambling, or financial advice, and users cannot place
              bets through the platform.
            </p>
            <p className="mt-3 text-white/70 text-sm md:text-base leading-relaxed">
              By creating an account, purchasing a subscription, or using our
              Service, you agree to these Terms of Use. If you do not agree with
              these Terms, please do not use the Service.
            </p>
          </div>

          {/* 1. Eligibility */}
          <div className="rounded-2xl border border-white/10 bg-white/5 p-5 md:p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-emerald-400/10 text-emerald-400">
                <FaUserCheck />
              </div>
              <h2 className="text-xl md:text-2xl font-semibold text-white">
                1. Eligibility
              </h2>
            </div>
            <div className="space-y-3 pl-0 md:pl-11">
              <div>
                <h3 className="text-white font-semibold text-base mb-1">
                  1.1 Age Requirement
                </h3>
                <p className="text-white/60 text-sm">
                  You must be at least 18 years old to create an account and use
                  the Service.
                </p>
              </div>
              <div>
                <h3 className="text-white font-semibold text-base mb-1">
                  1.2 Legal Compliance
                </h3>
                <p className="text-white/60 text-sm">
                  You are responsible for ensuring that use of the Service is
                  lawful in your country or jurisdiction.
                </p>
              </div>
            </div>
          </div>

          {/* 2. Description of the Service */}
          <div className="rounded-2xl border border-white/10 bg-white/5 p-5 md:p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-blue-400/10 text-blue-400">
                <FaDatabase />
              </div>
              <h2 className="text-xl md:text-2xl font-semibold text-white">
                2. Description of the Service
              </h2>
            </div>
            <p className="text-white/60 text-sm pl-0 md:pl-11">
              The Service provides, among other features: football match
              schedules and statistics, AI-generated match analyses, Daily Value
              Snapshots, premium value event recommendations, user-defined
              projects and performance tracking, historical reports and charts,
              and subscription management. The Service is intended solely for
              informational and statistical purposes.
            </p>
          </div>

          {/* 3. No Betting or Gambling Services */}
          <div className="rounded-2xl border border-amber-500/20 bg-amber-500/5 p-5 md:p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-amber-400/10 text-amber-400">
                <FaExclamationTriangle />
              </div>
              <h2 className="text-xl md:text-2xl font-semibold text-white">
                3. No Betting or Gambling Services
              </h2>
            </div>
            <div className="pl-0 md:pl-11 space-y-2">
              <p className="text-white/60 text-sm">The Service:</p>
              <ul className="space-y-1 text-white/60 text-sm">
                <li className="flex items-center gap-2">
                  • Does not accept wagers
                </li>
                <li className="flex items-center gap-2">
                  • Does not facilitate betting
                </li>
                <li className="flex items-center gap-2">
                  • Does not guarantee profit
                </li>
                <li className="flex items-center gap-2">
                  • Does not provide financial advice
                </li>
                <li className="flex items-center gap-2">
                  • Does not act as a bookmaker
                </li>
              </ul>
              <p className="text-white/60 text-sm mt-3">
                All predictions, probabilities, and value edges are generated
                using mathematical and AI models and are provided for
                informational purposes only.{" "}
                <span className="text-amber-400">
                  Past performance does not guarantee future results.
                </span>
              </p>
            </div>
          </div>

          {/* 4. Account Registration */}
          <div className="rounded-2xl border border-white/10 bg-white/5 p-5 md:p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-purple-400/10 text-purple-400">
                <FaShieldAlt />
              </div>
              <h2 className="text-xl md:text-2xl font-semibold text-white">
                4. Account Registration
              </h2>
            </div>
            <p className="text-white/60 text-sm pl-0 md:pl-11">
              To use the Service, you must create an account and select one of
              the available subscription plans. You agree to: provide accurate
              and complete information, keep your credentials secure, notify us
              immediately of unauthorized access, and be responsible for all
              activity under your account.
            </p>
          </div>

          {/* 5. Subscription Plans */}
          <div className="rounded-2xl border border-white/10 bg-white/5 p-5 md:p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-green-400/10 text-green-400">
                <FaCreditCard />
              </div>
              <h2 className="text-xl md:text-2xl font-semibold text-white">
                5. Subscription Plans
              </h2>
            </div>

            <p className="text-white/60 text-sm mb-6">
              At registration, you must choose one of the following plans:
            </p>

            <div className="grid gap-6 grid-cols-1 md:grid-cols-2 lg:grid-cols-4">
              {/* Free */}
              <div className="relative flex flex-col rounded-2xl border border-white/10 bg-white/5 p-6 transition-all hover:scale-[1.02] hover:border-white/20">
                <div className="mb-4 flex items-center gap-3">
                  <div className="rounded-xl bg-gray-400/10 p-2.5 text-gray-400">
                    <FaVial size={20} />
                  </div>
                  <h3 className="text-xl font-bold text-white">Free</h3>
                </div>
                <div className="mb-4">
                  <p className="text-white/60 text-sm">
                    3-Day free trial of the Basic plan
                  </p>
                </div>
                <ul className="space-y-2.5 text-sm text-white/60 flex-grow">
                  <li className="flex items-center gap-2">
                    <FaCheckCircle className="text-gray-400 text-xs shrink-0" />
                    <span>Calendar access</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <FaCheckCircle className="text-gray-400 text-xs shrink-0" />
                    <span>Unlimited projects</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <FaCheckCircle className="text-gray-400 text-xs shrink-0" />
                    <span>Today's matches</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <FaCheckCircle className="text-gray-400 text-xs shrink-0" />
                    <span>Basic statistics</span>
                  </li>
                </ul>
                <div className="mt-6 pt-4 border-t border-white/10 space-y-1 text-xs text-red-400/80">
                  <p>✗ No Daily Value Snapshots</p>
                  <p>✗ No AI Predictions</p>
                  <p>✗ No Premium Analysis</p>
                </div>
              </div>

              {/* Basic */}
              <div className="relative flex flex-col rounded-2xl border border-white/10 bg-white/5 p-6 transition-all hover:scale-[1.02] hover:border-white/20">
                <div className="absolute top-4 right-4 text-white/50 text-sm">
                  €4.99<span className="text-xs">/month</span>
                </div>
                <div className="mb-4 flex items-center gap-3">
                  <div className="rounded-xl bg-blue-400/10 p-2.5 text-blue-400">
                    <FaDatabase size={20} />
                  </div>
                  <h3 className="text-xl font-bold text-blue-400">Basic</h3>
                </div>
                <p className="mb-4 text-sm text-white/60">
                  Essential tracking and organizing
                </p>
                <ul className="space-y-2.5 text-sm text-white/60 flex-grow">
                  <li className="flex items-center gap-2">
                    <FaCheckCircle className="text-blue-400 text-xs shrink-0" />
                    <span>Calendar access</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <FaCheckCircle className="text-blue-400 text-xs shrink-0" />
                    <span>Unlimited projects</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <FaCheckCircle className="text-blue-400 text-xs shrink-0" />
                    <span>Today's matches</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <FaCheckCircle className="text-blue-400 text-xs shrink-0" />
                    <span>Basic statistics</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <FaCheckCircle className="text-blue-400 text-xs shrink-0" />
                    <span>Standard support</span>
                  </li>
                </ul>
                <div className="mt-6 pt-4 border-t border-white/10 space-y-1 text-xs text-red-400/80">
                  <p>✗ No Daily Value Snapshots</p>
                  <p>✗ No AI Predictions</p>
                  <p>✗ No Premium Analysis</p>
                </div>
              </div>

              {/* Pro */}
              <div className="relative flex flex-col rounded-2xl border border-white/10 bg-gradient-to-b from-emerald-400/5 to-transparent p-6 transition-all hover:scale-[1.02] hover:border-white/20">
                <div className="absolute top-4 right-4 text-white/50 text-sm">
                  €13.99<span className="text-xs">/month</span>
                </div>
                <div className="mb-4 flex items-center gap-3">
                  <div className="rounded-xl bg-emerald-400/10 p-2.5 text-emerald-400">
                    <FaGem size={20} />
                  </div>
                  <h3 className="text-xl font-bold text-emerald-400">Pro</h3>
                </div>
                <p className="mb-4 text-sm text-white/60">
                  Professional AI analysis
                </p>
                <ul className="space-y-2.5 text-sm text-white/60 flex-grow">
                  <li className="flex items-center gap-2">
                    <FaCheckCircle className="text-emerald-400 text-xs shrink-0" />
                    <span>Everything in Basic</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <FaCheckCircle className="text-emerald-400 text-xs shrink-0" />
                    <span>Daily Value Snapshots</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <FaCheckCircle className="text-emerald-400 text-xs shrink-0" />
                    <span>AI Predictions</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <FaCheckCircle className="text-emerald-400 text-xs shrink-0" />
                    <span>Advanced stats</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <FaCheckCircle className="text-emerald-400 text-xs shrink-0" />
                    <span>Priority support</span>
                  </li>
                </ul>
                <div className="mt-6 pt-4 border-t border-white/10 space-y-1 text-xs text-red-400/80">
                  <p>✗ No Premium Analysis</p>
                  <p>✗ Up to 50 daily AI matches</p>
                </div>
              </div>

              {/* Premium */}
              <div className="relative flex flex-col rounded-2xl border-2 border-purple-400/50 bg-gradient-to-b from-purple-400/10 to-transparent p-6 transition-all hover:scale-[1.02]">
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-purple-400 px-3 py-0.5 text-xs font-bold text-slate-950 whitespace-nowrap">
                  MOST POPULAR
                </div>
                <div className="absolute top-4 right-4 text-white/50 text-sm">
                  €19.99<span className="text-xs">/month</span>
                </div>
                <div className="mb-4 flex items-center gap-3 mt-2">
                  <div className="rounded-xl bg-purple-400/10 p-2.5 text-purple-400">
                    <FaCrown size={20} />
                  </div>
                  <h3 className="text-xl font-bold text-purple-400">Premium</h3>
                </div>
                <p className="mb-4 text-sm text-white/60">
                  Complete AI-powered analysis
                </p>
                <ul className="space-y-2.5 text-sm text-white/60 flex-grow">
                  <li className="flex items-center gap-2">
                    <FaCheckCircle className="text-purple-400 text-xs shrink-0" />
                    <span>Everything in Pro</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <FaCheckCircle className="text-purple-400 text-xs shrink-0" />
                    <span>Premium Analysis</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <FaCheckCircle className="text-purple-400 text-xs shrink-0" />
                    <span>Advanced Premium AI</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <FaCheckCircle className="text-purple-400 text-xs shrink-0" />
                    <span>Unlimited daily AI matches</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <FaCheckCircle className="text-purple-400 text-xs shrink-0" />
                    <span>VIP support</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* 6. Free Trial */}
          <div className="rounded-2xl border border-white/10 bg-white/5 p-5 md:p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-cyan-400/10 text-cyan-400">
                <FaSyncAlt />
              </div>
              <h2 className="text-xl md:text-2xl font-semibold text-white">
                6. Free Trial
              </h2>
            </div>
            <p className="text-white/60 text-sm pl-0 md:pl-11">
              New users selecting the Free Plan may receive a 3-day trial with
              limited access to selected features. We reserve the right to
              modify or discontinue the trial at any time.
            </p>
          </div>

          {/* 7. Billing and Payments */}
          <div className="rounded-2xl border border-white/10 bg-white/5 p-5 md:p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-emerald-400/10 text-emerald-400">
                <FaCreditCard />
              </div>
              <h2 className="text-xl md:text-2xl font-semibold text-white">
                7. Billing and Payments
              </h2>
            </div>
            <div className="pl-0 md:pl-11 space-y-4">
              <div className="rounded-lg border border-emerald-500/20 bg-emerald-500/5 p-4">
                <p className="text-emerald-300 text-sm font-semibold mb-2">
                  Important Information About Subscription Payments
                </p>
                <p className="text-white/70 text-sm">
                  Vision Royale operates on a{" "}
                  <span className="text-emerald-400 font-semibold">
                    pre-paid subscription model
                  </span>
                  . When you purchase a subscription plan, you gain access to
                  the Service for the duration of one (1) month from the
                  purchase date. There is{" "}
                  <span className="text-amber-400 font-semibold">
                    no automatic recurring billing
                  </span>{" "}
                  and{" "}
                  <span className="text-amber-400 font-semibold">
                    no recurring payments
                  </span>
                  .
                </p>
                <p className="text-white/70 text-sm mt-2">
                  Each month, you must manually renew your subscription by
                  making a new payment. If you do not renew, your access to paid
                  features will expire at the end of the current monthly period.
                </p>
              </div>

              <div>
                <h3 className="text-white font-semibold text-base mb-1">
                  7.1 Payment Processor
                </h3>
                <p className="text-white/60 text-sm">
                  Payments are processed securely through Stripe.
                </p>
              </div>

              <div>
                <h3 className="text-white font-semibold text-base mb-1">
                  7.2 Payment Terms
                </h3>
                <p className="text-white/60 text-sm">
                  Each subscription payment grants access for one month. No
                  automatic renewals occur.
                </p>
              </div>

              <div>
                <h3 className="text-white font-semibold text-base mb-1">
                  7.3 Failed Payments
                </h3>
                <p className="text-white/60 text-sm">
                  If a payment fails during the checkout process, access to the
                  paid plan will not be granted until payment is successfully
                  completed.
                </p>
              </div>

              <div>
                <h3 className="text-white font-semibold text-base mb-1">
                  7.4 Taxes
                </h3>
                <p className="text-white/60 text-sm">
                  Prices may be subject to VAT or other taxes depending on your
                  location.
                </p>
              </div>
            </div>
          </div>

          {/* 8. Cancellation and Refunds */}
          <div className="rounded-2xl border border-white/10 bg-white/5 p-5 md:p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-rose-400/10 text-rose-400">
                <FaGavel />
              </div>
              <h2 className="text-xl md:text-2xl font-semibold text-white">
                8. Cancellation and Refunds
              </h2>
            </div>
            <div className="pl-0 md:pl-11 space-y-3">
              <div>
                <h3 className="text-white font-semibold text-base mb-1">
                  8.1 No Recurring Billing to Cancel
                </h3>
                <p className="text-white/60 text-sm">
                  Since Vision Royale does not use automatic recurring billing,
                  there is no subscription to cancel. Your paid access
                  automatically expires at the end of your monthly period if you
                  choose not to renew.
                </p>
              </div>
              <div>
                <h3 className="text-white font-semibold text-base mb-1">
                  8.2 Refund Policy
                </h3>
                <p className="text-white/60 text-sm">
                  Unless required by law, payments are non-refundable.
                </p>
              </div>
              <div>
                <h3 className="text-white font-semibold text-base mb-1">
                  8.3 EU Right of Withdrawal
                </h3>
                <p className="text-white/60 text-sm">
                  If you are a consumer in the European Union, you may have a
                  14-day withdrawal right unless you expressly consent to
                  immediate access to digital content and acknowledge loss of
                  that right.
                </p>
              </div>
            </div>
          </div>

          {/* 9. Acceptable Use */}
          <div className="rounded-2xl border border-white/10 bg-white/5 p-5 md:p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-indigo-400/10 text-indigo-400">
                <FaCheckCircle />
              </div>
              <h2 className="text-xl md:text-2xl font-semibold text-white">
                9. Acceptable Use
              </h2>
            </div>
            <p className="text-white/60 text-sm pl-0 md:pl-11">
              You agree not to: share your account credentials, reverse engineer
              or copy the Service, use bots or automated scripts, scrape or
              harvest data, interfere with Service functionality, or use the
              Service for unlawful purposes.
            </p>
          </div>

          {/* 10. Intellectual Property */}
          <div className="rounded-2xl border border-white/10 bg-white/5 p-5 md:p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-yellow-400/10 text-yellow-400">
                <FaShieldAlt />
              </div>
              <h2 className="text-xl md:text-2xl font-semibold text-white">
                10. Intellectual Property
              </h2>
            </div>
            <p className="text-white/60 text-sm pl-0 md:pl-11">
              All content, software, algorithms, AI models, designs, trademarks,
              and databases are owned by us or our licensors and are protected
              by intellectual property laws. You receive a limited,
              non-exclusive, non-transferable license to use the Service for
              personal, non-commercial purposes.
            </p>
          </div>

          {/* 11. AI Analyses and Statistical Models */}
          <div className="rounded-2xl border border-white/10 bg-white/5 p-5 md:p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-purple-400/10 text-purple-400">
                <FaRobot />
              </div>
              <h2 className="text-xl md:text-2xl font-semibold text-white">
                11. AI Analyses and Statistical Models
              </h2>
            </div>
            <p className="text-white/60 text-sm pl-0 md:pl-11">
              AI-generated predictions and analyses are generated automatically,
              may contain inaccuracies, are not guaranteed, and should not be
              relied upon as financial advice. Users assume full responsibility
              for any decisions based on the information provided.
            </p>
          </div>

          {/* 12. Data Accuracy and Availability */}
          <div className="rounded-2xl border border-white/10 bg-white/5 p-5 md:p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-orange-400/10 text-orange-400">
                <FaChartLine />
              </div>
              <h2 className="text-xl md:text-2xl font-semibold text-white">
                12. Data Accuracy and Availability
              </h2>
            </div>
            <p className="text-white/60 text-sm pl-0 md:pl-11">
              We strive to provide accurate and timely data, but we do not
              guarantee accuracy, completeness, availability, continuous
              operation, or error-free functionality. Features may be modified,
              suspended, or discontinued at any time.
            </p>
          </div>

          {/* 13. Third-Party Data Providers */}
          <div className="rounded-2xl border border-white/10 bg-white/5 p-5 md:p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-teal-400/10 text-teal-400">
                <FaGlobe />
              </div>
              <h2 className="text-xl md:text-2xl font-semibold text-white">
                13. Third-Party Data Providers
              </h2>
            </div>
            <p className="text-white/60 text-sm pl-0 md:pl-11">
              The Service may use data from third-party APIs and providers,
              including sports data providers and payment processors. We are not
              responsible for errors, delays, or interruptions caused by
              third-party services.
            </p>
          </div>

          {/* 14. Termination */}
          <div className="rounded-2xl border border-white/10 bg-white/5 p-5 md:p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-red-400/10 text-red-400">
                <FaExclamationTriangle />
              </div>
              <h2 className="text-xl md:text-2xl font-semibold text-white">
                14. Termination
              </h2>
            </div>
            <p className="text-white/60 text-sm pl-0 md:pl-11">
              We may suspend or terminate your account if you violate these
              Terms, payment fails, fraudulent or abusive activity is detected,
              or required by law.
            </p>
          </div>

          {/* 15. Changes to These Terms */}
          <div className="rounded-2xl border border-white/10 bg-white/5 p-5 md:p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-yellow-400/10 text-yellow-400">
                <FaSyncAlt />
              </div>
              <h2 className="text-xl md:text-2xl font-semibold text-white">
                15. Changes to These Terms
              </h2>
            </div>
            <p className="text-white/60 text-sm pl-0 md:pl-11">
              We may update these Terms at any time. Material changes will be
              communicated through the website or email. Continued use of the
              Service after changes become effective constitutes acceptance of
              the updated Terms.
            </p>
          </div>

          {/* 16. Contact Information */}
          <div className="rounded-2xl border border-emerald-500/30 bg-emerald-500/5 p-5 md:p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-emerald-400/10 text-emerald-400">
                <FaEnvelope />
              </div>
              <h2 className="text-xl md:text-2xl font-semibold text-white">
                16. Contact Information
              </h2>
            </div>
            <p className="text-white/60 text-sm pl-0 md:pl-11">
              If you have questions regarding these Terms, please contact us
              through the Contact page available within the Service or by email.
            </p>
          </div>
        </div>

        {/* Acceptance Footer */}
        <div className="mt-8 md:mt-12 rounded-2xl border border-white/10 bg-white/5 p-5 md:p-6 text-center">
          <p className="text-white/50 text-xs md:text-sm">
            By using Vision Royale, you acknowledge that you have read,
            understood, and agree to be bound by these Terms of Use.
          </p>
          <p className="text-white/40 text-xs mt-2">
            Vision Royale — AI-Powered Value Edge Engine Platform
          </p>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-white/10 bg-slate-950/80">
        <div className="mx-auto max-w-7xl px-4 py-6 md:py-8 md:px-6">
          <div className="flex flex-col items-center justify-between gap-3 md:gap-4 md:flex-row">
            <div className="text-center text-xs text-white/40 md:text-left">
              © 2026 Vision Royale. AI-Powered Value Edge Engine Platform.
            </div>
            <div className="flex gap-5 md:gap-6 text-xs md:text-sm text-white/50">
              <button
                onClick={handleTermsOfUse}
                className="hover:text-white transition-colors"
              >
                Terms Of Use
              </button>
              <button
                onClick={handlePrivacy}
                className="hover:text-white transition-colors"
              >
                Privacy
              </button>
              <button
                onClick={handleContact}
                className="hover:text-white transition-colors"
              >
                Contact
              </button>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
